import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { animate } from 'animejs';
import ProductCard from '../components/ProductCard';
import FramedImage from '../components/FramedImage';
import { getCategoryLabel } from '../lib/catalogCategories';
import { resolveHomepageData } from '../lib/homepageData';

const fallbackProductImage = 'https://images.unsplash.com/photo-1584992236310-6edddc08acff?auto=format&fit=crop&w=1200&q=85';
const heroImage = (product) => product?.cover_image || product?.images?.[0] || fallbackProductImage;
const DEFAULT_ANNOUNCEMENT = 'Handmade pieces, shipped with care.';
const BROWSE_CATEGORY_SLUGS = new Set(['crochet', 'gifts']);

const hasBadge = (product, badge) => String(product?.badges || '').toLowerCase().includes(badge);

function getSafeAnnouncement(value) {
  const announcement = String(value || '').replace(/\s+/g, ' ').trim();
  if (!announcement) return DEFAULT_ANNOUNCEMENT;

  return /free shipping/i.test(announcement) ? DEFAULT_ANNOUNCEMENT : announcement;
}

export default function HomePage() {
  const [sections, setSections] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [loadNotice, setLoadNotice] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [heroIndex, setHeroIndex] = useState(0);
  const [heroInteractionPaused, setHeroInteractionPaused] = useState(false);
  const carouselRef = useRef(null);
  const carouselAnimationsRef = useRef([]);
  const heroTouchStartRef = useRef(null);
  const heroSwipeRef = useRef(false);

  const heroProducts = products
    .filter((product) => product.featured || product.badges?.includes('bestseller') || product.badges?.includes('new'))
    .concat(products.filter((product) => !product.featured && !product.badges?.includes('bestseller') && !product.badges?.includes('new')))
    .filter((product, index, list) => list.findIndex((item) => item.id === product.id) === index);
  const isHeroPaused = heroInteractionPaused;
  const announcement = getSafeAnnouncement(settings.announcement_text);

  useEffect(() => {
    const controller = new AbortController();

    async function loadData() {
      setLoadError('');
      setLoadNotice('');
      setLoading(true);
      try {
        const fetchJson = async (url) => {
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) throw new Error(`Could not load ${url}.`);
          return response.json();
        };
        const results = await Promise.allSettled([
          fetchJson('/api/cms/homepage'),
          fetchJson('/api/products'),
          fetchJson('/api/categories'),
          fetchJson('/api/settings'),
        ]);
        const homepageData = resolveHomepageData(results);

        setSections(homepageData.sections);
        setProducts(homepageData.products);
        setCategories(homepageData.categories);
        setSettings(homepageData.settings);
        if (homepageData.hasOptionalFailure) {
          setLoadNotice('Some storefront details could not be loaded. Products are still available.');
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('Failed to load homepage data:', err);
          setLoadError('We could not load the storefront. Check your connection and try again.');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    loadData();
    return () => controller.abort();
  }, [loadAttempt]);

  useEffect(() => {
    if (heroProducts.length < 2 || isHeroPaused || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setHeroIndex((current) => (current + 1) % heroProducts.length);
    }, 4800);

    return () => window.clearInterval(interval);
  }, [heroProducts.length, isHeroPaused]);

  useEffect(() => {
    if (heroIndex >= heroProducts.length) setHeroIndex(0);
  }, [heroIndex, heroProducts.length]);

  useEffect(() => {
    const root = carouselRef.current;
    if (!root || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    carouselAnimationsRef.current.forEach((animation) => animation.cancel());

    const media = root.querySelector('.product-carousel-hero__media');
    const image = root.querySelector('.product-carousel-hero__media img');

    const animations = [
      animate(media, { opacity: [0, 1], scale: [0.94, 1], translateX: [-26, 0], duration: 760, ease: 'out(5)' }),
      animate(image, { scale: [1.16, 1], rotate: [-1.5, 0], duration: 1100, ease: 'out(4)' }),
    ];

    carouselAnimationsRef.current = animations;

    return () => animations.forEach((animation) => animation.cancel());
  }, [heroIndex, heroProducts.length]);

  const handleHeroTouchStart = (event) => {
    const touch = event.touches[0];
    heroSwipeRef.current = false;
    if (touch) heroTouchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleHeroTouchEnd = (event) => {
    const start = heroTouchStartRef.current;
    const touch = event.changedTouches[0];
    heroTouchStartRef.current = null;
    if (!start || !touch || heroProducts.length < 2) return;

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY)) return;

    heroSwipeRef.current = true;
    setHeroIndex((current) => (
      current + (deltaX < 0 ? 1 : -1) + heroProducts.length
    ) % heroProducts.length);
  };

  const handleHeroMediaClick = (event) => {
    if (!heroSwipeRef.current) return;
    event.preventDefault();
    heroSwipeRef.current = false;
  };

  if (loading) {
    return (
      <div className="container text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading Hunar…</span>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <main className="container py-5">
        <section className="storefront-error" role="alert">
          <h1 className="brand-font">Storefront unavailable</h1>
          <p>{loadError}</p>
          <button type="button" className="btn btn-primary-custom" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
            Try again
          </button>
        </section>
      </main>
    );
  }

  if (products.length === 0) {
    return (
      <main className="container py-5">
        <section className="storefront-empty" role="status">
          <h1 className="brand-font">Fresh handmade pieces are on the way</h1>
          <p>There are no products to browse right now. Please check back soon.</p>
          <button type="button" className="btn btn-primary-custom" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
            Refresh products
          </button>
        </section>
      </main>
    );
  }

  const bestSellers = products.filter((product) => hasBadge(product, 'bestseller')).slice(0, 4);
  const newArrivals = products
    .filter((product) => hasBadge(product, 'new'))
    .filter((product) => !bestSellers.some((bestSeller) => bestSeller.id === product.id))
    .slice(0, 4);
  const browseCategories = categories.filter((category) => BROWSE_CATEGORY_SLUGS.has(category.slug));
  const sectionsWithoutTestimonials = sections.filter((section) => section.section_type !== 'testimonials');
  const orderedSections = sectionsWithoutTestimonials;

  return (
    <div className="homepage-container">
      {/* Announcement Banner */}
      {settings.announcement_enabled === 'true' && announcement && (
        <div className="homepage-announcement text-center py-2 px-3 small font-weight-bold shadow-sm">
          {announcement}
        </div>
      )}

      {loadNotice && (
        <div className="container pt-3">
          <div className="storefront-notice" role="status">
            <span>{loadNotice}</span>
            <button type="button" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Dynamic Sections Rendered from Database CMS */}
      {orderedSections.map((section) => {
        switch (section.section_type) {
          case 'hero':
            if (heroProducts.length === 0) return null;

            const product = heroProducts[heroIndex];
            return (
              <section
                key={section.id}
                className="product-carousel-hero container"
                ref={carouselRef}
                aria-roledescription="carousel"
                aria-label="Featured handmade products. Automatic rotation pauses while you inspect the showcase."
                aria-live="off"
                onMouseEnter={() => setHeroInteractionPaused(true)}
                onMouseLeave={() => setHeroInteractionPaused(false)}
                onFocusCapture={() => setHeroInteractionPaused(true)}
                onBlurCapture={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) setHeroInteractionPaused(false);
                }}
              >
                <div className="product-carousel-hero__content">
                  <div
                    className="product-carousel-hero__media"
                    onTouchStart={handleHeroTouchStart}
                    onTouchEnd={handleHeroTouchEnd}
                    onTouchCancel={() => { heroTouchStartRef.current = null; }}
                  >
                    <Link
                      to={`/product/${product.slug}`}
                      className="product-carousel-hero__media-link"
                      aria-label={`View ${product.title || 'featured handmade product'}`}
                      onClick={handleHeroMediaClick}
                    >
                      <FramedImage
                        src={heroImage(product)}
                        alt={product.title || 'Featured handmade product'}
                        fallbackSrc={fallbackProductImage}
                        loading="eager"
                      />
                    </Link>
                    <div className="product-carousel-hero__dots" aria-label="Featured product slides">
                      {heroProducts.map((heroProduct, index) => (
                        <button
                          key={heroProduct.id}
                          type="button"
                          className={`product-carousel-hero__dot${index === heroIndex ? ' is-active' : ''}`}
                          onClick={() => setHeroIndex(index)}
                          aria-label={`Show featured product ${index + 1}`}
                          aria-current={index === heroIndex ? 'true' : undefined}
                        >
                          <span aria-hidden="true" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            );

          case 'categories':
            if (browseCategories.length === 0) return null;
            return (
              <section key={section.id} className="my-5 container">
                <div className="homepage-section-heading mb-4">
                  <h2 className="brand-font">{section.title || 'Browse by Category'}</h2>
                  <p className="text-muted">{section.subtitle || 'Find the perfect handmade creation'}</p>
                </div>

                <div className="homepage-category-list">
                  {browseCategories.map((cat) => (
                    <Link key={cat.id} to={`/shop?category=${cat.slug}`} className="homepage-category-link text-decoration-none text-reset">
                      <span className="homepage-category-name">{getCategoryLabel(cat)}</span>
                    </Link>
                  ))}
                </div>
              </section>
            );

          case 'best_sellers':
            if (bestSellers.length === 0) return null;
            return (
              <section key={section.id} className="my-5 container">
                <div className="d-flex justify-content-between align-items-end mb-4">
                  <div>
                    <h2 className="brand-font mb-1">{section.title || 'Bestsellers'}</h2>
                    <p className="text-muted m-0">{section.subtitle || 'Most loved by our community'}</p>
                  </div>
                  <Link to="/shop" className="text-primary font-weight-bold text-decoration-none">
                    View All →
                  </Link>
                </div>

                <div className={`homepage-product-grid homepage-product-grid--${bestSellers.length}`}>
                  {bestSellers.map((prod) => (
                    <div key={prod.id}>
                      <ProductCard product={prod} />
                    </div>
                  ))}
                </div>
              </section>
            );

          case 'new_arrivals':
            if (newArrivals.length === 0) return null;
            return (
              <section key={section.id} className="my-5 container">
                <div className="d-flex justify-content-between align-items-end mb-4">
                  <div>
                    <h2 className="brand-font mb-1">{section.title || 'New Arrivals'}</h2>
                    <p className="text-muted m-0">{section.subtitle || 'Fresh off the craft table'}</p>
                  </div>
                  <Link to="/shop" className="text-primary font-weight-bold text-decoration-none">
                    View All →
                  </Link>
                </div>

                <div className={`homepage-product-grid homepage-product-grid--${newArrivals.length}`}>
                  {newArrivals.map((prod) => (
                    <div key={prod.id}>
                      <ProductCard product={prod} />
                    </div>
                  ))}
                </div>
              </section>
            );

          default:
            return null;
        }
      })}
    </div>
  );
}
